import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";
import ServiceBento from "../components/ServiceBento";
import CaptionImage from "../components/CaptionImage";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { Field, SubmitButton, useSubmission } from "../components/Forms";
import { createCheckout } from "../lib/api";
import { IMAGES } from "../site/content";

const SECTIONS = [
  {
    id: "transcription",
    number: "01",
    title: "Transcription",
    intro:
      "Transcription converts audio and video into clear, well-formatted text. Interviews, podcasts, webinars, meetings, legal and academic recordings. Accuracy and confidentiality first.",
    deliverables: [
      "Verbatim or clean-read transcripts",
      "Timestamps and speaker labels",
      "Interviews, podcasts, and webinars",
      "Meeting and board minutes",
      "Legal and academic recordings",
      "Confidential handling by default",
    ],
    img: IMAGES.transcription,
  },
  {
    id: "translation",
    number: "02",
    title: "Translation",
    lead: "Translation",
    intro:
      "Our team delivers precise, culturally appropriate translations across major languages. We handle documents, websites, marketing materials, subtitles, and technical content so your message stays clear and professional in every market.",
    deliverables: [
      "Business and legal documents",
      "Websites and product copy",
      "Marketing materials",
      "Subtitles and captions",
      "Technical documentation",
      "Native-speaker review",
    ],
    img: IMAGES.translation,
  },
  {
    id: "media",
    number: "03",
    title: "Media Content",
    lead: "Media content",
    intro:
      "We produce engaging videos, social reels, promotional films, motion graphics, and branded stories that capture attention and communicate your vision. Every project is built to reflect your brand and connect with the people you need to reach.",
    deliverables: [
      "Promotional and brand films",
      "Social reels and shorts",
      "Motion graphics",
      "Voiceover and sound design",
      "Podcast production",
      "Branded story series",
    ],
    img: IMAGES.media,
  },
  {
    id: "advertising",
    number: "04",
    title: "Digital Advertising",
    lead: "Digital advertising",
    intro:
      "We design and manage targeted campaigns on Google, Meta, and YouTube. Smart strategy, creative assets, and ongoing optimisation to generate leads, lift conversions, and improve return on investment.",
    deliverables: [
      "Campaign strategy and planning",
      "Google Ads management",
      "Meta (Facebook / Instagram) Ads",
      "YouTube Ads",
      "Creative asset production",
      "Ongoing optimisation and reporting",
    ],
    img: IMAGES.advertising,
  },
];

const PACKAGES = [
  {
    id: "transcription-starter",
    title: "Transcription Deposit",
    price: 75,
    subtitle: "Fast-track retainer for initial audio/video files",
    features: ["Up to 60 audio minutes included", "Verbatim or clean transcript", "Speaker timestamps included", "Delivered within 48 hours"],
  },
  {
    id: "translation-starter",
    title: "Translation Starter",
    price: 150,
    subtitle: "Document / web page translation kickoff",
    features: ["Up to 1,000 words in any target language", "Human native review", "Cultural context check", "Formatted delivery"],
  },
  {
    id: "campaign-kickoff",
    title: "Media / Ads Kickoff",
    price: 300,
    subtitle: "Initial campaign strategy and creative brief deposit",
    features: ["Target audience audit", "Initial ad copy & asset plan", "Channel allocation strategy", "Applied toward full campaign"],
  },
];

function PackageCheckout() {
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const { checkout_url } = await createCheckout({
        kind: "service",
        amount: pkg.price,
        method: "Credit Card",
        note: pkg.title,
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoadingId(null);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-3" data-testid="service-packages-grid">
      {PACKAGES.map((pkg) => (
        <div
          key={pkg.id}
          className="flex flex-col justify-between rounded-3xl border border-line bg-white/80 p-6 sm:p-8 shadow-sm transition-transform duration-300 hover:-translate-y-1"
          data-testid={`package-card-${pkg.id}`}
        >
          <div>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal">Instant Retainer</span>
            <h3 className="mt-2 font-serif text-2xl font-bold text-charcoal">{pkg.title}</h3>
            <p className="mt-1 text-xs text-charcoal-muted">{pkg.subtitle}</p>
            <div className="my-6 border-y border-line py-4">
              <span className="font-serif text-4xl font-bold text-teal">${pkg.price}</span>
              <span className="ml-2 text-xs font-mono text-charcoal/60">USD deposit</span>
            </div>
            <ul className="space-y-2.5">
              {pkg.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-xs sm:text-sm text-charcoal/80">
                  <Check size={16} className="shrink-0 text-teal mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8">
            <button
              onClick={() => startCheckout(pkg)}
              disabled={loadingId === pkg.id}
              className="btn-primary w-full text-sm"
              data-testid={`checkout-btn-${pkg.id}`}
            >
              <CreditCard size={16} />
              {loadingId === pkg.id ? "Opening Stripe…" : `Pay $${pkg.price} via Stripe`}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function BriefForm() {
  const { sending, sent, send } = useSubmission("campaign_brief");
  const [form, setForm] = useState({ name: "", email: "", subject: "Transcription", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (sent) {
    return (
      <div className="rounded-3xl border border-teal/30 bg-teal/5 p-8 text-center" data-testid="brief-success">
        <p className="font-serif text-2xl text-teal">Brief received.</p>
        <p className="mt-3 text-sm text-charcoal-muted">
          A project owner will reply with next steps and a free consultation slot.
        </p>
      </div>
    );
  }

  return (
    <form
      className="rounded-3xl border border-line bg-white/70 p-6 sm:p-9"
      data-testid="brief-form"
      onSubmit={(e) => {
        e.preventDefault();
        send(form);
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Your name" htmlFor="brief-name">
          <input id="brief-name" required className="field" value={form.name} onChange={set("name")} data-testid="brief-name-input" placeholder="First name is fine" />
        </Field>
        <Field label="Email" htmlFor="brief-email">
          <input id="brief-email" required type="email" className="field" value={form.email} onChange={set("email")} data-testid="brief-email-input" placeholder="you@company.com" />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Service" htmlFor="brief-service">
          <select id="brief-service" className="field" value={form.subject} onChange={set("subject")} data-testid="brief-service-select">
            <option>Transcription</option>
            <option>Translation</option>
            <option>Media Content</option>
            <option>Digital Advertising</option>
            <option>A mix of services</option>
          </select>
        </Field>
      </div>
      <div className="mt-5">
        <Field label="The brief" htmlFor="brief-message">
          <textarea id="brief-message" required rows={5} className="field" value={form.message} onChange={set("message")} data-testid="brief-message-input" placeholder="What do you need made, translated, or promoted? Rough volume, languages, deadlines — anything helps." />
        </Field>
      </div>
      <div className="mt-7">
        <SubmitButton sending={sending} testid="brief-submit-button">Start the brief</SubmitButton>
      </div>
    </form>
  );
}

export default function Services() {
  return (
    <>
      <Seo
        title="Services — Transcription, Translation, Media & Advertising"
        description="Transcription that reads clean, translation that survives contact with a new market, media that gets watched, and advertising measured in results."
        path="/services"
      />

      <section className="pt-28 sm:pt-36 pb-14 sm:pb-20" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-teal" aria-hidden="true" />
            Services
          </p>
          <MaskedLines
            lines={["Work that reads well,", <span key="t" className="italic text-teal">travels far, converts.</span>]}
            className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-charcoal-muted">
              Every engagement has a named owner, a clear deliverables list, and a
              turnaround you can plan around. Start with a free consultation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-24" data-testid="services-bento-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ServiceBento />
        </div>
      </section>

      {SECTIONS.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`py-16 sm:py-24 ${i % 2 === 1 ? "bg-sand/60 border-y border-line" : ""}`}
          data-testid={`service-section-${s.id}`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Chapter number={s.number} eyebrow="Service" title={s.title} />
            <div className={`grid items-start gap-10 lg:grid-cols-12`}>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <Reveal>
                  <p className="text-base sm:text-lg leading-relaxed text-charcoal">
                    {s.lead ? (
                      <>
                        <strong className="font-serif text-xl text-teal">{s.lead}</strong>
                        <span className="mx-2 text-charcoal/40">—</span>
                        {s.intro}
                      </>
                    ) : (
                      s.intro
                    )}
                  </p>
                  <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-teal">Deliverables</p>
                  <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {s.deliverables.map((d) => (
                      <li key={d} className="flex gap-3 text-sm leading-relaxed text-charcoal/90">
                        <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rotate-45 bg-teal" aria-hidden="true" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <Reveal delay={0.1}>
                  <CaptionImage image={s.img} ratio="aspect-[4/3]" rounded="rounded-[2rem]" />
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="py-16 sm:py-24 bg-sand/40 border-t border-line" data-testid="packages-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="05" eyebrow="Quick Retainers" title="Ready to start right away? Pay an initial deposit." />
          <Reveal>
            <p className="mb-10 max-w-2xl text-sm sm:text-base leading-relaxed text-charcoal-muted">
              Book your project immediately through secure Stripe checkout, or request an itemised invoice below.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <PackageCheckout />
          </Reveal>
          <div className="mt-8 text-center">
            <Link to="/support?tab=pay" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-teal hover:underline" data-testid="custom-invoice-link">
              Need a custom amount? Pay any invoice amount here <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      <section id="brief" className="py-16 sm:py-24 bg-teal-dark" data-testid="brief-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12 items-start">
            <div className="lg:col-span-5">
              <Chapter number="06" eyebrow="Start a brief" title="Tell us what needs to exist." dark />
              <Reveal delay={0.1}>
                <p className="text-sm sm:text-base leading-relaxed text-cream/75">
                  Two minutes of writing saves two weeks of back-and-forth. Share the
                  shape of the project — we reply with a plan, a timeline, and a price.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <BriefForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
